import {
  computed,
  onMounted,
  onScopeDispose,
  ref,
  toValue,
  watch,
  type MaybeRefOrGetter
} from 'vue';
import { useQuery, useQueryClient } from '@tanstack/vue-query';
import { isBoolean } from 'lodash';

import { t, type Maybe } from '@nova/core';
import { useDebounce } from '@satellite/composables/useDebounce';
import { useEventHub } from '@satellite/composables/useEventHub';
import { useNotify } from '@satellite/composables/useNotify';
import { DEFAULT_INVALIDATE_OPTIONS, queryKeys } from '@satellite/composables/queryKeys';
import companyService from '@satellite/services/company-service';

import type { CarrierCompanyDetails } from '@satellite/composables/carrierPresenterShared';

const MINIMUM_SEARCH_LENGTH = 3;
const SEARCH_DEBOUNCE_MILLISECONDS = 550;
const SEARCH_LIMIT = 15;
const COMPANY_TOPICS = ['create-Company', 'update-Company'] as const;

export interface UseCompanySelectSearchOptions {
  selectedCompany?: MaybeRefOrGetter<Maybe<CarrierCompanyDetails>>;
}

export function useCompanySelectSearch({ selectedCompany }: UseCompanySelectSearchOptions = {}) {
  const { notify } = useNotify();
  const eventHub = useEventHub();
  const queryClient = useQueryClient();
  const search = ref('');
  const debouncedSearch = ref('');
  const isInViewport = ref(false);
  const pendingInvalidation = ref(false);

  const {
    trigger: debounceSearch,
    cancel: cancelDebouncedSearch,
    isPending
  } = useDebounce((nextSearch: string) => {
    debouncedSearch.value = nextSearch;
  }, SEARCH_DEBOUNCE_MILLISECONDS);

  const searchQualifies = computed(() => debouncedSearch.value.length >= MINIMUM_SEARCH_LENGTH);
  const isSearchBelowMinimum = computed(
    () => search.value.length > 0 && search.value.length < MINIMUM_SEARCH_LENGTH
  );
  const companyListQueryKey = computed(() => queryKeys.companyList(debouncedSearch.value));

  const companyListQuery = useQuery({
    queryKey: companyListQueryKey,
    queryFn: async ({ signal }) => {
      const currentSearch = debouncedSearch.value;

      if (currentSearch.length < MINIMUM_SEARCH_LENGTH) {
        return [];
      }

      const response = await companyService.autocomplete(
        { limit: SEARCH_LIMIT, search: currentSearch },
        { signal }
      );
      const companies = Array.isArray(response?.items) ? response.items : [];

      return companies as CarrierCompanyDetails[];
    },
    enabled: computed(() => searchQualifies.value && isInViewport.value),
    staleTime: 30_000,
    refetchOnWindowFocus: false
  });

  const data = computed(() => {
    if (isSearchBelowMinimum.value) {
      return [];
    }

    const companies = (companyListQuery.data.value as CarrierCompanyDetails[] | undefined) ?? [];
    const currentCompany = selectedCompany ? toValue(selectedCompany) : null;

    /** A free-text company (no `id`) is not a search result, so it is never listed. */
    if (!currentCompany?.id || companies.some(company => company.id === currentCompany.id)) {
      return companies;
    }

    return [currentCompany, ...companies];
  });

  /**
   * The query key changes on every debounced search, so a backend outage would
   * otherwise toast once per keystroke burst. One toast per failing streak,
   * re-armed only after a search succeeds.
   */
  const hasNotifiedSearchFailure = ref(false);

  watch(
    () => companyListQuery.status.value,
    status => {
      if (status === 'success') {
        hasNotifiedSearchFailure.value = false;
        return;
      }

      if (status === 'error' && !hasNotifiedSearchFailure.value) {
        hasNotifiedSearchFailure.value = true;
        notify(t('forms.elements.selects.companySelect.messages.searchFailed'), 'error');
      }
    }
  );

  const invalidate = () => {
    queryClient.invalidateQueries(
      {
        queryKey: queryKeys.companyLists()
      },
      DEFAULT_INVALIDATE_OPTIONS
    );
  };

  const deferOrInvalidate = () => {
    if (!isInViewport.value) {
      pendingInvalidation.value = true;
      return;
    }

    invalidate();
  };

  const onIntersect = (entry?: { isIntersecting?: boolean } | boolean) => {
    isInViewport.value = isBoolean(entry) ? entry : (entry?.isIntersecting ?? true);

    if (isInViewport.value && pendingInvalidation.value) {
      pendingInvalidation.value = false;
      invalidate();
    }
  };

  const updateSearch = (value: string | null) => {
    const normalizedSearch = String(value ?? '').trim();
    search.value = normalizedSearch;

    if (!normalizedSearch || normalizedSearch.length < MINIMUM_SEARCH_LENGTH) {
      cancelDebouncedSearch();
      debouncedSearch.value = normalizedSearch;
      return;
    }

    debounceSearch(normalizedSearch);
  };

  onMounted(() => {
    COMPANY_TOPICS.forEach(topic => eventHub.on(topic, deferOrInvalidate));
  });

  onScopeDispose(() => {
    COMPANY_TOPICS.forEach(topic => eventHub.off(topic, deferOrInvalidate));
  });

  return {
    data,
    error: computed(() => companyListQuery.error.value),
    isLoading: computed(() => companyListQuery.isFetching.value || isPending.value),
    isSearchBelowMinimum,
    minimumSearchLength: MINIMUM_SEARCH_LENGTH,
    onIntersect,
    search,
    updateSearch
  };
}
